import React, { useState } from "react";
import {
  IconEye,
  IconChevronDown,
  IconChevronUp,
  IconMapPin,
  IconBox,
  IconPrinter,
  IconUser,
  IconTruck,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { T, useLanguage } from "../../../context/LanguageContext";

type Shipment = {
  id: string;
  sn: number;
  orderType: string;
  createdAt: string;
  platform: string;
  awbNumber: string;
  referenceNumber: string;
  warehouseName: string;
  routeCode: string;
  shippingCompany: string;
  shippingAwbNumber: string;
  senderCityName: string;
  receiverCityName: string;
  receiverCountryName: string;
  receiverDistrictName: string;
  receiverNationalAddress: string;
  receiverAddress?: string;
  receiverName: string;
  receiverMobile: string;
  receiverEmail?: string;
  paymentType: string;
  codAmount: number;
  status: string;
  updatedAt: string;
  dispatchStatus: string;
  clientName: string;
  outletName?: string;
  senderName?: string;
  olineItems: {
    itemName: string;
    sku: string;
    quantity: number;
    weight: number;
    price: number;
    binLocation: string;
    inventory: number;
    description?: string;
  }[];
};

type Props = {
  data: {
    orders: Shipment[];
  };
  columnsData: any[];
  currentPage: number;
  limitPage: number;
  isCheckboxEnabled: boolean;
  selectedOrders: Shipment[];
  onSelectOrder: (order: Shipment) => void;
  onViewDetails?: (awb: string) => void;
};

const getStatusBadge = (status: string) => {
  const normStatus = (status || "").toUpperCase();
  switch (normStatus) {
    case "NEW_ORDER":
      return {
        label: "New Order",
        className: "bg-emerald-50 border border-emerald-300 text-emerald-800",
      };
    case "ORDER_GENERATED":
      return {
        label: "Order Generated",
        className: "bg-teal-50 border border-teal-300 text-teal-800",
      };
    case "ORDER_CREATED":
      return {
        label: "Order Created",
        className: "bg-sky-50 border border-sky-300 text-sky-800",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        className: "bg-indigo-50 border border-indigo-300 text-indigo-800",
      };
    case "READY_FOR_PICKUP":
    case "READY_TO_PICK":
      return {
        label: "Ready For Pickup",
        className: "bg-yellow-50 border border-yellow-300 text-yellow-800",
      };
    case "PACKED":
      return {
        label: "Packed",
        className: "bg-violet-50 border border-violet-300 text-violet-800",
      };
    case "DISPATCHED":
      return {
        label: "Dispatched",
        className: "bg-fuchsia-50 border border-fuchsia-300 text-fuchsia-800",
      };
    case "IN_TRANSIT":
      return {
        label: "In Transit",
        className: "bg-cyan-50 border border-cyan-300 text-cyan-800",
      };
    case "OUT_FOR_DELIVERY":
      return {
        label: "Out For Delivery",
        className: "bg-orange-50 border border-orange-300 text-orange-800",
      };
    case "DELIVERED":
      return {
        label: "Delivered",
        className: "bg-lime-50 border border-lime-300 text-lime-800",
      };
    case "UNDELIVERED":
      return {
        label: "Undelivered",
        className: "bg-amber-50 border border-amber-300 text-amber-800",
      };
    case "RETURNED":
      return {
        label: "Returned",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "REVERSE_NEW":
      return {
        label: "Reverse New",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "DELETED":
      return {
        label: "Deleted",
        className: "bg-rose-50 border border-rose-300 text-rose-800",
      };
    case "FAILED":
      return {
        label: "Failed",
        className: "bg-red-50 border border-red-300 text-red-800",
      };
    case "LOST":
      return {
        label: "Lost",
        className: "bg-stone-50 border border-stone-300 text-stone-700",
      };
    case "DRAFT":
      return {
        label: "Draft",
        className: "bg-zinc-50 border border-zinc-300 text-zinc-700",
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        className: "bg-slate-50 border border-slate-300 text-slate-700",
      };
    case "RECEIVE_IN_WAREHOUSE":
      return {
        label: "Receive In Warehouse",
        className: "bg-blue-50 border border-blue-300 text-blue-800",
      };
    case "PICKUP_GENERATED":
      return {
        label: "Pickup Generated",
        className: "bg-green-50 border border-green-300 text-green-800",
      };
    case "RETURN_TO_SHIPPER":
      return {
        label: "Return To Shipper",
        className: "bg-rose-100 border border-rose-300 text-rose-800",
      };
    case "FORWARD_TO_3PL":
      return {
        label: "Forward To 3PL",
        className: "bg-purple-100 border border-purple-300 text-purple-800",
      };
    case "DISPATCHED_TO_3PL":
      return {
        label: "Dispatched To 3PL",
        className: "bg-violet-100 border border-violet-300 text-violet-800",
      };
    case "PICKUP_COLLECTED":
      return {
        label: "Pickup Collected",
        className: "bg-teal-100 border border-teal-300 text-teal-800",
      };
    case "PICKLIST_GENERATED":
      return {
        label: "Picklist Generated",
        className: "bg-indigo-100 border border-indigo-300 text-indigo-800",
      };
    case "PICKED":
      return {
        label: "Picked",
        className: "bg-sky-100 border border-sky-300 text-sky-800",
      };
    case "RECEIVE_INBOUND":
      return {
        label: "Receive Inbound",
        className: "bg-cyan-100 border border-cyan-300 text-cyan-800",
      };
    case "SHELVE":
      return {
        label: "Shelve",
        className: "bg-emerald-100 border border-emerald-300 text-emerald-800",
      };
    case "PENDING":
      return {
        label: "Pending",
        className: "bg-amber-100 border border-amber-300 text-amber-800",
      };
    case "PENDING_STOCK":
      return {
        label: "Pending Stock",
        className: "bg-orange-100 border border-orange-300 text-orange-800",
      };
    default:
      const label = status
        ? status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ")
        : "Unknown";
      return {
        label,
        className: "bg-slate-50 border border-slate-200 text-slate-600",
      };
  }
};

const AllOrderTable: React.FC<Props> = ({
  data,
  columnsData,
  selectedOrders,
  onSelectOrder,
  onViewDetails,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const maxStatusWidth = React.useMemo(() => {
    if (!data?.orders?.length) return "100px";
    const maxLen = data.orders.reduce((max, item) => {
      const label = getStatusBadge(item.status).label || "";
      return label.length > max ? label.length : max;
    }, 0);
    const calculatedWidth = Math.max(80, maxLen * 7.2 + 24);
    return `${calculatedWidth}px`;
  }, [data?.orders]);

  const isColShow = (val: string) => {
    if (!columnsData || columnsData.length === 0) return true;
    return columnsData.find((col) => col.value === val)?.isShow !== false;
  };

  const toggleRow = (id: string) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const handleConfirmDeduct = (sku: string) => {
    toast.success(`Inventory deducted successfully for SKU: ${sku}!`);
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const months = [
      t("Jan"), t("Feb"), t("Mar"), t("Apr"),
      t("May"), t("Jun"), t("Jul"), t("Aug"),
      t("Sep"), t("Oct"), t("Nov"), t("Dec"),
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const handlePrintPickSlip = (order: any) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Failed to open print window. Please allow popups.");
      return;
    }

    const itemsHtml =
      order.olineItems
        ?.map((item: any) => {
          return `
        <tr>
          <td style="font-family: monospace; font-weight: bold; border-bottom: 1px solid #eee; padding: 10px 12px;">${item.sku}</td>
          <td style="border-bottom: 1px solid #eee; padding: 10px 12px;">${item.itemName}</td>
          <td style="text-align: center; font-weight: bold; border-bottom: 1px solid #eee; padding: 10px 12px;">${item.quantity}</td>
          <td style="font-weight: 500; border-bottom: 1px solid #eee; padding: 10px 12px;">${item.binLocation}</td>
        </tr>
      `;
        })
        .join("") ||
      '<tr><td colspan="4" style="text-align: center; padding: 15px; color: #888;">No items found</td></tr>';

    const htmlContent = `
      <html>
      <head>
        <title>Pick Slip - ${order.awbNumber}</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            padding: 30px;
            margin: 0;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 3px solid #FBD037;
            padding-bottom: 15px;
            margin-bottom: 30px;
          }
          .title {
            font-size: 26px;
            font-weight: 800;
            color: #131327;
            letter-spacing: -0.5px;
          }
          .meta-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
          }
          .meta-box {
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 16px;
            background: #f9fafb;
          }
          .meta-box h4 {
            margin: 0 0 10px 0;
            color: #4b5563;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 700;
          }
          .meta-box p {
            margin: 6px 0;
            font-size: 14px;
            color: #1f2937;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 25px;
          }
          th {
            background-color: #FBD037;
            color: #131327;
            font-weight: 700;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 12px;
            text-align: left;
          }
          .footer {
            margin-top: 60px;
            text-align: center;
            font-size: 12px;
            color: #9ca3af;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">LAST MILE PICK SLIP</div>
            <p style="margin: 4px 0 0 0; font-size: 14px; color: #4b5563;">Order Pick List Document</p>
          </div>
          <div style="text-align: right;">
            <div style="font-weight: 800; font-size: 18px; color: #131327;">AWB: ${order.awbNumber}</div>
            <div style="font-size: 13px; color: #6b7280; margin-top: 4px; font-family: monospace;">Ref: ${order.referenceNumber || "N/A"}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-box">
            <h4>Merchant (Origin)</h4>
            <p><strong>Merchant:</strong> ${order.clientName}</p>
            <p><strong>Origin Location:</strong> ${order.warehouseName}</p>
            <p><strong>Order Date:</strong> ${new Date(order.createdAt).toLocaleDateString()}</p>
          </div>
          <div class="meta-box">
            <h4>Receiver Details</h4>
            <p><strong>Name:</strong> ${order.receiverName}</p>
            <p><strong>Mobile:</strong> ${order.receiverMobile}</p>
            <p><strong>Destination:</strong> ${order.receiverCityName}, ${order.receiverDistrictName}</p>
          </div>
        </div>

        <h3 style="color: #131327; font-size: 16px; font-weight: 700; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Allocated Items List</h3>
        <table>
          <thead>
            <tr>
              <th style="border-top-left-radius: 6px; border-bottom-left-radius: 6px;">SKU</th>
              <th>Item Description</th>
              <th style="text-align: center;">Ordered Qty</th>
              <th style="border-top-right-radius: 6px; border-bottom-right-radius: 6px;">Stock Location</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <div class="footer">
          Generated on ${new Date().toLocaleString()} | Powered by Last Mile Logistics Systems
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="bg-[#F7F6F2] rounded-3xl">
      <div className="overflow-y-auto overflow-x-auto max-h-[75vh] custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner">
        {data.orders.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-slate-400 text-sm font-semibold">No orders found matching the filter</p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse table-fixed min-w-[1200px]">
            <colgroup>
              <col style={{ width: "2%" }} />
              {isColShow("sn") && <col style={{ width: "2%" }} />}
              {isColShow("tag") && <col style={{ width: "6%" }} />}
              {isColShow("createdDate") && <col style={{ width: "8%" }} />}
              {isColShow("platform") && <col style={{ width: "5%" }} />}
              {isColShow("awbRef") && <col style={{ width: "8.5%" }} />}
              {isColShow("warehouse") && <col style={{ width: "7.5%" }} />}
              {isColShow("route") && <col style={{ width: "6%" }} />}
              {isColShow("shippingDetails") && <col style={{ width: "7.5%" }} />}
              {isColShow("origin") && <col style={{ width: "5.5%" }} />}
              {isColShow("destination") && <col style={{ width: "8%" }} />}
              {isColShow("receiverDetails") && <col style={{ width: "8%" }} />}
              {isColShow("paymentInfo") && <col style={{ width: "6%" }} />}
              {isColShow("merchant") && <col style={{ width: "8%" }} />}
              {isColShow("status") && <col style={{ width: "7%" }} />}
              {isColShow("actions") && (
                <>
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "4%" }} />
                </>
              )}
            </colgroup>

            <thead className="text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 bg-[#FCFDF8]">
              <tr className="text-center text-[11px] font-semibold tracking-wider uppercase select-none">
                {/* Checkbox */}
                <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200">
                  <div className="flex items-center justify-center">
                    <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
                      <input
                        type="checkbox"
                        checked={data.orders.length > 0 && selectedOrders.length === data.orders.length}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          data.orders.forEach((order) => {
                            const isSelected = selectedOrders.some((o) => o.id === order.id);
                            if (checked && !isSelected) onSelectOrder(order);
                            else if (!checked && isSelected) onSelectOrder(order);
                          });
                        }}
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
                      />
                      <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
                        ✓
                      </span>
                    </label>
                  </div>
                </th>

                {isColShow("sn") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center"><T>S/N</T></th>
                )}
                {isColShow("tag") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Tag</T></th>
                )}
                {isColShow("createdDate") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 whitespace-nowrap"><T>Date</T></th>
                )}
                {isColShow("platform") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Platform</T></th>
                )}
                {isColShow("awbRef") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>AWB / Ref</T></th>
                )}
                {isColShow("warehouse") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Warehouse</T></th>
                )}
                {isColShow("route") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Route</T></th>
                )}
                {isColShow("shippingDetails") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>3PL Shipping</T></th>
                )}
                {isColShow("origin") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Origin</T></th>
                )}
                {isColShow("destination") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Destination</T></th>
                )}
                {isColShow("receiverDetails") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Receiver</T></th>
                )}
                {isColShow("paymentInfo") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Payment</T></th>
                )}
                {isColShow("merchant") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200"><T>Merchant</T></th>
                )}
                {isColShow("status") && (
                  <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center"><T>Status</T></th>
                )}
                {isColShow("actions") && (
                  <>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center"><T>Items</T></th>
                    <th className="px-2 pt-3 pb-2 text-center"><T>View</T></th>
                  </>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 bg-white">
              {data.orders.map((item, idx) => {
                const isExpanded = expandedRow === item.id;
                const isSelected = selectedOrders.some((o) => o.id === item.id);

                return (
                  <React.Fragment key={`${item.id}-${item.paymentType || idx}`}>
                    <tr
                      className={`hover:bg-[#FBD037]/10 group transition-colors duration-150 ${isSelected ? "bg-amber-50/40" : ""
                        }`}
                    >
                      {/* Checkbox */}
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-center">
                          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => onSelectOrder(item)}
                              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
                            />
                            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
                              ✓
                            </span>
                          </label>
                        </div>
                      </td>

                      {/* S/N */}
                      {isColShow("sn") && (
                        <td className="px-2 py-3 text-xs font-semibold text-[#212121]">
                          {idx + 1}
                        </td>
                      )}

                      {/* TAG */}
                      {isColShow("tag") && (
                        <td className="px-2 py-3 min-w-0">
                          <span
                            className={`px-1.5 py-0.5 rounded-md text-[10px] font-black tracking-wide border uppercase whitespace-nowrap ${item.orderType === "FM"
                              ? "bg-sky-50 border-sky-200 text-sky-700"
                              : "bg-purple-50 border-purple-200 text-purple-700"
                              }`}
                          >
                            <T>{item.orderType}</T>
                          </span>
                        </td>
                      )}

                      {/* DATE */}
                      {isColShow("createdDate") && (
                        <td className="px-2 py-3 min-w-0 whitespace-nowrap">
                          <div className="text-xs font-bold text-[#212121]">
                            {formatDate(item.createdAt)}
                          </div>
                        </td>
                      )}

                      {/* PLATFORM */}
                      {isColShow("platform") && (
                        <td className="px-2 py-3 min-w-0">
                          {item.platform ? (
                            <span className="inline-block bg-slate-100 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-bold max-w-full" data-tooltip={t(item.platform)}>
                              <T>{item.platform}</T>
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}

                      {/* AWB NO. / REF */}
                      {isColShow("awbRef") && (
                        <td className="px-2 py-3 min-w-0">
                          <div className="text-xs font-bold text-[#212121]" data-tooltip={item.awbNumber}>
                            {item.awbNumber}
                          </div>
                          <div className="text-[10px] text-gray-400 font-medium mt-0.5" data-tooltip={`${t("ref:")} ${item.referenceNumber}`}>
                            {t("ref:")} {item.referenceNumber}
                          </div>
                        </td>
                      )}

                      {/* WAREHOUSE */}
                      {isColShow("warehouse") && (
                        <td className="px-2 py-3 min-w-0 text-center">
                          <div
                            className="text-xs font-medium text-[#212121] truncate"
                            data-tooltip={item.platform === "OUTLET" ? item.outletName || item.warehouseName : item.warehouseName}
                          >
                            {item.platform === "OUTLET" ? item.outletName || item.warehouseName : item.warehouseName}
                          </div>
                        </td>
                      )}

                      {/* ROUTE */}
                      {isColShow("route") && (
                        <td className="px-2 py-3 text-center">
                          {item.routeCode ? (
                            <span className="inline-flex items-center bg-[#FBD037]/20 border border-[#FBD037]/40 text-[#856608] px-1.5 py-0.5 rounded text-[10px] font-black max-w-full truncate" data-tooltip={item.routeCode}>
                              {item.routeCode}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}

                      {/* 3PL Shipping */}
                      {isColShow("shippingDetails") && (
                        <td className="px-2 py-3 min-w-0 text-center">
                          <div className="text-xs font-bold text-[#212121] truncate" data-tooltip={item.shippingCompany}>
                            {item.shippingCompany}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate" data-tooltip={item.shippingAwbNumber}>
                            {item.shippingAwbNumber}
                          </div>
                        </td>
                      )}

                      {/* Origin */}
                      {isColShow("origin") && (
                        <td className="px-2 py-3 min-w-0 text-center">
                          {(() => {
                            const originParts = (item.senderCityName || '').split(' ');
                            const originCity = originParts[0] || '';
                            const originWhse = originParts.slice(1).join(' ');
                            return (
                              <>
                                <div className="text-xs font-bold text-[#212121] truncate" data-tooltip={t(originCity)}>
                                  <T>{originCity}</T>
                                </div>
                                {originWhse && (
                                  <div className="text-[10px] text-slate-400 font-medium truncate" data-tooltip={t(originWhse)}>
                                    <T>{originWhse}</T>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      )}

                      {/* Destination */}
                      {isColShow("destination") && (
                        <td className="px-2 py-3 min-w-0 max-w-0 overflow-hidden">
                          {/* City */}
                          <div
                            className="text-xs font-bold text-[#212121] truncate"
                            data-tooltip={`${t(item.receiverCityName)}${item.receiverCountryName ? `, ${t(item.receiverCountryName)}` : ""}`}
                          >
                            <T>{item.receiverCityName}</T>
                            {item.receiverCountryName ? `, ` : ""}
                            {item.receiverCountryName && <T>{item.receiverCountryName}</T>}
                          </div>

                          {/* District + Short Address */}
                          <div className="text-[9px] text-slate-400 font-bold flex items-center gap-1 mt-1">
                            <span
                              className="truncate"
                              data-tooltip={`${t("dist:")} ${t(item.receiverDistrictName)}`}
                            >
                              {t("dist:")} <T>{item.receiverDistrictName}</T>
                            </span>

                            {item.receiverNationalAddress && (
                              <span
                                className="border border-slate-200 bg-slate-50 text-slate-500 px-1 rounded font-mono text-[9px] max-w-[80px]"
                                data-tooltip={item.receiverNationalAddress}
                              >
                                {item.receiverNationalAddress}
                              </span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Receiver */}
                      {isColShow("receiverDetails") && (
                        <td className="px-2 py-3 min-w-0 max-w-0 overflow-hidden text-center">
                          <div className="text-xs font-bold text-[#212121] truncate" data-tooltip={item.receiverName}>
                            {item.receiverName}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5 truncate" data-tooltip={item.receiverMobile}>
                            {item.receiverMobile}
                          </div>
                        </td>
                      )}

                      {/* Payment */}
                      {isColShow("paymentInfo") && (
                        <td className="px-2 py-3 min-w-0 text-center">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-black tracking-wider uppercase border inline-block max-w-full ${item.paymentType === "COD"
                              ? "bg-amber-50 border-amber-200 text-amber-800"
                              : "bg-emerald-50 border-emerald-200 text-emerald-800"
                              }`}
                            data-tooltip={item.paymentType === "COD" ? `${t("COD")}: ${item.codAmount ?? 0}` : t("CC: PAID")}
                          >
                            {item.paymentType === "COD" ? `${t("COD")}: ${item.codAmount ?? 0}` : t("CC: PAID")}
                          </span>
                        </td>
                      )}

                      {/* Merchant */}
                      {isColShow("merchant") && (
                        <td className="px-2 py-3 min-w-0 text-center">
                          <span
                            className="text-xs font-black text-[#856608] block truncate"
                            data-tooltip={item.platform === "OUTLET" ? item.senderName || item.clientName : item.clientName}
                          >
                            {item.platform === "OUTLET" ? item.senderName || item.clientName : item.clientName}
                          </span>
                        </td>
                      )}

                      {/* Status */}
                      {isColShow("status") && (
                        <td className="px-2 py-3 text-center">
                          {(() => {
                            const badge = getStatusBadge(item.status);
                            return (
                              <span
                                style={{ width: maxStatusWidth }}
                                className={`border py-0.5 rounded-full text-[10px] font-extrabold tracking-wide inline-block text-center px-2 truncate max-w-full align-middle ${badge.className}`}
                                data-tooltip={t(badge.label)}
                              >
                                <T>{badge.label}</T>
                              </span>
                            );
                          })()}
                        </td>
                      )}

                      {/* Items */}
                      {isColShow("actions") && (
                        <td className="px-2 py-3 text-center">
                          {(item.olineItems?.length ?? 0) > 0 && (
                            <button
                              onClick={() => toggleRow(item.id)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-extrabold rounded-full border transition-all duration-150 select-none shadow-2xs whitespace-nowrap ${isExpanded
                                ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                                : "bg-indigo-50 border-indigo-100 hover:border-indigo-300 text-indigo-700 hover:bg-indigo-100/60"
                                }`}
                            >
                              <span>
                                {item.olineItems?.length ?? 0}{" "}
                                <T>{(item.olineItems?.length ?? 0) === 1 ? "Item" : "Items"}</T>
                              </span>
                            </button>
                          )}
                        </td>
                      )}

                      {/* View */}
                      {isColShow("actions") && (
                        <td className="px-2 py-3 text-center">
                          <button
                            onClick={() => onViewDetails ? onViewDetails(item.awbNumber) : navigate(`/orders/${item.awbNumber}`)}
                            className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors"
                          >
                            <IconEye size={13} stroke={2.5} />
                          </button>
                        </td>
                      )}
                    </tr>

                    {/* Expandable Accordion Row: Item Detail Allocation Spectrum */}
                    {isExpanded && (
                      <tr className="bg-transparent border-none">
                        <td colSpan={100} className="px-6 py-1 bg-slate-50/30">
                          <div className="border border-[#FBD037] rounded-3xl bg-slate-50/60 shadow-xs p-6 my-4 mx-4 space-y-5 text-left">
                            {/* Header block */}
                            <div className="flex items-start gap-2.5">
                              <div className="p-2 bg-amber-50 border border-amber-100 rounded-xl text-amber-600 shadow-2xs">
                                <IconBox size={18} stroke={2.5} />
                              </div>
                              <div>
                                <h4 className="font-extrabold text-slate-800 text-sm tracking-tight leading-none">
                                  <T>Item Detail Allocation Spectrum</T>
                                </h4>
                                <p className="text-[11px] text-slate-400 font-bold mt-1.5">
                                  <T>Verify stock location routing schema and allocate items below.</T>
                                </p>
                              </div>
                            </div>

                            {/* Grid lists of Allocated Items */}
                            <div className="space-y-3.5">
                              {item.olineItems.map((lineItem, lineIdx) => (
                                <div
                                  key={lineIdx}
                                  className="bg-white border border-slate-100/90 rounded-2xl px-6 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-5 transition hover:shadow-sm"
                                >
                                  <div className="space-y-1.5 w-48 pl-6">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest block leading-none">
                                      <T>SKU</T>
                                    </span>
                                    <div className="text-[#FF8D06] font-extrabold text-sm tracking-tight leading-none">
                                      {lineItem.sku}
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 w-64">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest block leading-none">
                                      <T>Item name</T>
                                    </span>
                                    <div className="text-xs font-bold text-slate-700 leading-tight">
                                      {lineItem.itemName}
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 w-48">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest block leading-none">
                                      <T>Location code</T>
                                    </span>
                                    {lineItem.binLocation ? (
                                      <div className="flex items-center gap-1.5 text-xs font-extrabold text-slate-600 mt-1">
                                        <IconMapPin
                                          size={15}
                                          stroke={2.5}
                                          className="text-amber-500"
                                        />
                                        <span className="font-mono">{lineItem.binLocation}</span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </div>
                                  <div className="space-y-1.5 text-center w-24">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest block leading-none">
                                      <T>Ordered qty</T>
                                    </span>
                                    <div className="inline-flex items-center justify-center bg-slate-900 text-white rounded-full w-7 h-7 font-black text-xs mt-1 select-none">
                                      {lineItem.quantity}
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 w-80">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest block leading-none uppercase">
                                      <T>Description</T>
                                    </span>
                                    <div className="text-slate-600 font-semibold text-xs mt-1 leading-normal break-words">
                                      {lineItem.description || ""}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AllOrderTable;
